/**
 * PropertyPanel — 지도에서 현장 선택 시 뜨는 정보밀도 패널 (Phase 7, additive).
 *
 * 호갱노노식: 마운트 시 상세 API 를 fetch 해 sections 스택을 SectionRenderer 로 렌더한다.
 * 지도(랜딩 /)는 절대 건드리지 않고, 그 위에 오버레이로만 얹는다.
 *
 * 구성 (scratchpad/panel_mock.html):
 *   - 상단 요약: 상태칩 · 현장명 · 주소 · 가격 + "자세히 보기"(상세페이지 Link) + 닫기(X)
 *   - 본문: detail.sections 를 SectionRenderer 로 렌더 (key==='summary' 는 상단 요약이 담당 → 제외)
 *   - 하단: StickyCtaBar (전화/카톡)
 *
 * 정책:
 *   - 순수 오버레이. 지도 상태·마커·selectedProperty 로직과 독립.
 *   - Concept A 토큰만. 카톡 referrerPolicy 는 StickyCtaBar 가 유지.
 *   - 로컬 dev 는 media 파일이 없어 이미지가 깨져 보이는 게 정상(코드 문제 아님).
 */

import { canContactProperty } from '../../domain/inquiry/contactPolicy';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SkeletonBlock } from '../../design-system/components/Skeleton';
import { propertyApi, Property } from '../../services/api';
import { getPropertyUrl } from '../../utils/formatters';
import { summaryData } from '../../domain/property/sectionAdapters';
import { dropDuplicatePriceSection } from '../../domain/property/detailV2';
import { resolveContactPhone } from '../../domain/inquiry/resolveContactPhone';
import { propertyCategory, setTrackingProperty } from '../../utils/tracking';
import {
  PropertyStatusChip,
  SectionRenderer,
  StickyCtaBar,
  SectionDescriptor,
} from '../../design-system/components/section';

export interface PropertyPanelProps {
  propertyId: number | string;
  onClose: () => void;
}

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" aria-hidden>
    <line x1="6" y1="6" x2="18" y2="18" />
    <line x1="18" y1="6" x2="6" y2="18" />
  </svg>
);

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" width={15} height={15} fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

export function PropertyPanel({ propertyId, onClose }: PropertyPanelProps) {
  const [detail, setDetail] = useState<Property | null>(null);
  const [status, setStatus] = useState<'loading' | 'error' | 'ready'>('loading');

  useEffect(() => {
    let alive = true;
    setStatus('loading');
    setDetail(null);
    propertyApi
      .getDetail(propertyId)
      .then((data) => {
        if (!alive) return;
        setDetail(data);
        setStatus('ready');
      })
      .catch(() => {
        if (!alive) return;
        setStatus('error');
      });
    return () => {
      alive = false;
    };
  }, [propertyId]);

  // 패널의 전화·카톡 클릭 Contact 이벤트 대상 현장 (닫으면 목록 공통값 '대구 할인분양 목록')
  useEffect(() => {
    if (!detail) return undefined;
    setTrackingProperty({ id: detail.id, name: detail.title, category: propertyCategory(detail.property_type) });
    return () => setTrackingProperty(null);
  }, [detail]);

  // 요약 섹션(summary)은 상단 요약 헤더가 담당하므로 본문 스택에서 제외.
  // 할인 분양가 안내 커스텀 섹션이 있으면 자동 분양가는 중복이라 제외 (대표 지시 2026-09-22, 상세와 동일 규칙)
  const sections: SectionDescriptor[] = detail
    ? dropDuplicatePriceSection(
        (((detail as any).sections ?? []) as SectionDescriptor[]).filter(
          (s) => !(s.kind === 'auto' && s.key === 'summary'),
        ),
      )
    : [];

  const summary = detail ? summaryData(detail) : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="현장 정보"
      className="fixed inset-0 z-[60] flex flex-col bg-paper lg:inset-y-0 lg:left-auto lg:right-0 lg:w-[440px] lg:border-l lg:border-ca-line"
    >
      {/* 상단 요약 헤더 (sticky) */}
      <div className="flex items-start gap-2 border-b border-ca-line bg-frame px-4 pb-3 pt-3">
        <div className="min-w-0 flex-1">
          {status === 'ready' && summary ? (
            <div className="flex flex-col gap-[5px]">
              <div>
                <PropertyStatusChip status={summary.status} isPreRegistration={summary.isPreRegistration} isReopen={summary.isReopen} />
              </div>
              <h2 className="text-[17px] font-extrabold leading-tight tracking-tight text-ink line-clamp-2">
                {summary.title}
              </h2>
              <div className="text-[12px] text-muted line-clamp-1">{summary.address}</div>
              <div className="text-[15px] font-extrabold text-accent-dark">{summary.priceLabel}</div>
              <div className="mt-0.5">
                <Link
                  to={getPropertyUrl(detail as Property)}
                  className="inline-flex items-center gap-1 rounded-ca-md border border-accent-line bg-accent-50 px-2.5 py-1 text-[12px] font-extrabold text-accent-dark"
                >
                  자세히 보기
                  <ArrowIcon />
                </Link>
              </div>
            </div>
          ) : (
            <div className="text-[13px] font-semibold text-muted">현장 정보</div>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="닫기"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-ca-line bg-frame text-ink"
        >
          <CloseIcon />
        </button>
      </div>

      {/* 본문 — 세로 스크롤 섹션 스택. 하단 CTA 바 높이만큼 여백. */}
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto bg-paper px-2.5 py-2.5 pb-[84px] [&>*]:shrink-0">
        {/* v2 S1 — 스켈레톤 (스피너 금지) */}
        {status === 'loading' && (
          <div role="status" className="flex flex-col gap-2">
            <span className="sr-only">정보를 불러오는 중...</span>
            <SkeletonBlock className="h-[120px] w-full !rounded-ca-3xl" />
            <SkeletonBlock className="h-[160px] w-full !rounded-ca-3xl" />
            <SkeletonBlock className="h-[120px] w-full !rounded-ca-3xl" />
          </div>
        )}

        {status === 'error' && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 py-16 text-center">
            <span className="text-[13px] font-semibold text-ink">정보를 불러오지 못했어요</span>
            <span className="text-[12px] text-muted">잠시 후 다시 시도해 주세요.</span>
            <button
              type="button"
              onClick={onClose}
              className="mt-1 rounded-ca-md bg-accent px-4 py-2 text-[12.5px] font-extrabold text-white shadow-ca-cta"
            >
              닫기
            </button>
          </div>
        )}

        {status === 'ready' && detail && (
          <>
            {sections.map((s) => (
              <SectionRenderer key={(s as any).id ?? s.key} section={s} property={detail} />
            ))}
          </>
        )}
      </div>

      {/* 하단 CTA — 전화/카톡 (referrerPolicy 는 StickyCtaBar 가 유지).
          2026-09-26 대표: 특별(담당자) 현장 외에는 담당자가 없다 — 카톡·1533-8124 로 연결되면 안 된다.
          상세페이지와 같게 is_side_inquiry(담당자 없음)면 숨김. 정보를 다 받기 전엔 그리지 않는다(잠깐 번쩍임 방지). */}
      {status === 'ready' && canContactProperty(detail) && detail && (
        <StickyCtaBar inline phone={resolveContactPhone(detail)} />
      )}
      {/* 담당자 없는 현장(대구·경북 외)은 전화·카톡·'문의 남기기' 버튼 없음 — 상세 하단 사이드 문의 폼만 (2026-10-06 대표) */}
    </div>
  );
}

export default PropertyPanel;
